// read-helpers.ts — caster-blob read helpers shared across PC-sheet tab renderers.
//
// The bucket readers ftLayerValue / ftLayerHas / ftItemsCol (item-arr-readers.js)
// are the FE twins of the PHP layer/items readers, BUT they deliberately do NOT
// reproduce the computed.creature.* BRIDGE that PHP layer_value() carries
// ([[feedback_ftlayervalue_no_computed_bridge]]). Several PC-sheet values that
// PHP reads via layer_value() actually live in a computed cluster
// (computed.creature.<cluster>.<key>, scalar OR {total: n}). computedCreature()
// mirrors that bridge: read the cluster slot, unwrap {total}; the CALLER supplies
// the ftLayerValue() fall-through to match PHP's "bridge null → carrier walk".

import type { CachedCaster } from './types';

/**
 * Read computed.creature.<cluster>.<key>, unwrapping a {total} wrapper. Returns
 * undefined when the slot is absent so the caller can fall back to ftLayerValue()
 * exactly like the PHP bridge falls through to the layer carrier walk.
 */
export function computedCreature(caster: CachedCaster, cluster: string, key: string): unknown {
    const bucket = caster?.computed?.creature?.[cluster];
    if (!bucket || typeof bucket !== 'object' || Array.isArray(bucket)) return undefined;
    const slot = (bucket as Record<string, unknown>)[key];
    if (slot === undefined || slot === null) return undefined;
    if (typeof slot === 'object' && !Array.isArray(slot) && 'total' in (slot as Record<string, unknown>)) {
        return (slot as Record<string, unknown>).total;
    }
    return slot;
}

/** Unwrap a PhpScalar-ish value ([v] | v) to its scalar; undefined/null pass through. */
export function unwrap(v: unknown): unknown {
    return Array.isArray(v) ? v[0] : v;
}

/** Number coercion that tolerates strings / wrapped scalars; non-finite → fallback. */
export function num(v: unknown, fallback = 0): number {
    const n = Number(unwrap(v));
    return Number.isFinite(n) ? n : fallback;
}

/** Mirror of PHP preg_replace('/[^A-Za-z0-9_]/', '', $id) for DOM-id building. */
export function sanitizeDomId(id: string): string {
    return String(id).replace(/[^A-Za-z0-9_]/g, '');
}

/**
 * Bootstrap badge classes for a carry-weight value, intensifying with load:
 *   < encumbered → green (safe) · ≥ encumbered → amber · ≥ heavily → red · ≥ max → dark (restrained).
 * Text color is paired for contrast (amber → dark text; the rest → white), plus bg-gradient for sheen.
 * The thresholds mirror the meter's swatch colors so the "now" badge matches the zone it's in. Kept in
 * sync with the PHP fallback (open_inventory_weight_breakdown_inc.php).
 */
export function carryWeightBadgeClasses(currentLb: number, encLb: number, heavyLb: number, maxLb: number): string {
    if (maxLb > 0 && currentLb >= maxLb) return 'bg-dark text-white bg-gradient border border-dark';
    if (heavyLb > 0 && currentLb >= heavyLb) return 'bg-danger text-white bg-gradient border border-dark';
    if (encLb > 0 && currentLb >= encLb) return 'bg-warning text-dark bg-gradient border border-dark';
    return 'bg-success text-white bg-gradient border border-dark';
}

/**
 * Read a column from the caster's player_characters bucket (bare scalar, [v]-tolerant).
 * Twin of PHP `$caster_arr['player_characters'][$key]`. undefined when absent/empty-array.
 */
export { readPlayerCharacterField as pcField } from '../janus-engine/display/player-character-field';

/**
 * The caster's identity item_type — the authoritative PC-vs-creature discriminator.
 *
 * A caster blob carries NO top-level `items` bucket: after the item_arr bucket reshape, `item_type`
 * rides in `encounter_creatures`, which `ftItemsCol` does NOT read (it only walks items/flat/status/
 * inventory-template). So `ftItemsCol(caster,'item_type')` returns `undefined` on a caster blob and any
 * `=== 'player_character'` gate built on it silently fails — which is exactly what suppressed the haven
 * buttons, SP columns, etc. Read the ec bucket FIRST, fall back to ftItemsCol
 * only for non-encounter shells. ([[reference_pc_creature_item_type_discriminator]])
 */
export function casterItemType(caster: CachedCaster): string {
    const ec = caster?.encounter_creatures as Record<string, unknown> | undefined;
    const raw = unwrap(ec?.item_type);
    if (raw !== undefined && raw !== null && raw !== '') return String(raw);
    const fallback = typeof ftItemsCol === 'function' ? ftItemsCol(caster, 'item_type') : undefined;
    return String(fallback ?? '');
}

/** True when the caster is a player character. Gate for PC-only sheet UI (haven return,
 *  skill points, hunger/thirst meters, …). Do NOT gate on the player_characters bucket alone — summons
 *  carry a PC-shaped struct. See {@link casterItemType} for why ftItemsCol alone is wrong here. */
export function isPlayerCharacter(caster: CachedCaster): boolean {
    return casterItemType(caster) === 'player_character';
}
